// src/components/FormWizard.js

import React, { useState, useEffect } from "react";
import ProgressBar from "components/ProgressBar/ProgressBar";
import ResultsComponent from "components/ResultsComponent/ResultsComponent";
import ImageUpload from "components/ImageUpload/ImageUpload";
import TypeaheadInput from "components/TypeaheadInput/TypeaheadInput";
import MultiSelectTypeahead from "components/MultiSelectTypeAhead/MultiSelectTypeAhead";
import MultiCarouselSelect from "components/MutliCarouselSelect/MultiCarouselSelect";
import LoadingScreen from "components/LoadingScreen/LoadingScreen";
import NavigationButtons from "components/NavigationButtons/NavigationButtons";
import "components/FormWizard/FormWizard.css";
import config from "config.js";

import whoYouAreLeft from "assets/background_images/Tell-Us-Who-You-Are_Left_Polaroid_300x600.svg";
import whoYouAreRight from "assets/background_images/Tell-Us-Who-You-Are_Right_Polaroid_300x600.svg";

import expressYourselfLeft from "assets/background_images/Tell-Us-How-You-Express-Yourself_Left_Polaroid_300x600.svg";
import expressYourselfRight from "assets/background_images/Tell-Us-How-You-Express-Yourself_Right_Polaroid_300x600.svg";

import whatInterestsYouLeft from "assets/background_images/Tell-Us-What-Interests-You_Left_Polaroid_300x600.svg";
import whatInterestsYouRight from "assets/background_images/Tell-Us-What-Interests-You_Right_Polaroid_300x600.svg";

import yourPassionLeft from "assets/background_images/Tell-Us-Your-Passion_Left_Polaroid_300x600.svg";
import yourPassionRight from "assets/background_images/Tell-Us-Your-Passion_Right_Polaroid_300x600.svg";

import whereYouSeeYourselfLeft from "assets/background_images/Tell-Us-Where-You-See-Yourself_Left_Polaroid_300x600.svg";
import whereYouSeeYourselfRight from "assets/background_images/Tell-Us-How-You-Express-Yourself_Right_Polaroid_300x600.svg";

import blackTextLogo from "assets/logos/gustavus_reg_mark_dark.svg";
import whiteTextLogo from "assets/logos/gustavus_reg_mark_light.svg";
import { placeholder } from "@babel/types";

const StepEnum = {
  ImageUpload: 0,
  Characteristics: 1,
  Major: 2,
  Activity: 3,
  Location: 4,
};

function FormWizard({
  setLogoImage,
  showFormWizard,
  setAppBackgroundColor,
  questions,
}) {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({
    image: "",
    ethnicity: "",
    characteristics: "",
    major: "",
    activity: "",
    location: "",
  });
  
  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(true);
  const [showResults, setShowResults] = useState(false);
  const [openAIResponse, setOpenAIResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setError] = useState([]);
  const [answerKey, setAnswerKey] = useState("");
  const [showError, setShowError] = useState(false);
  const [placeholder, setPlaceHolder] = useState();
  const [carouselType, setCarouselType] = useState(""); //? using this to manage state between nav buttons and carousel (it sucks I know)

  const errorCallback = (error) => {
    const newErrors = [...errors];
    newErrors.push(error);
    setError(newErrors);
  };

  // * callback
  const handleStartNewDesign = () => {
    setOpenAIResponse(null);
    setCurrentStep(0);
    setShowResults(false);
    setAnswers({
      image: "",
      ethnicity: "",
      gender: "",
      characteristics: "",
      major: "",
      activity: "",
      location: "",
    });
    setAppBackgroundColor("white");
    setLogoImage(blackTextLogo);
    setCarouselType("");
  };

  //* callback
  const handleImageSelect = (base64Image) => {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      ["image"]: base64Image,
    }));
  };

  // * the user doesnt upload a headshot, set lion to generate an image with a lion
  const handleSkip = () => {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      ["image"]: "lion",
      ["ethnicity"]: "",
    }));
    nextStep();
  };

  //* callback
  const ethnicitySelectCallback = (ethnicity) => {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      ["ethnicity"]: ethnicity,
    }));
  };

    //* callback
    const genderSelectCallback = (gender) => {
      setAnswers((prevAnswers) => ({
        ...prevAnswers,
        ["gender"]: gender,
      }));
    };

  //* callback
  const handleTypeAheadSelect = (event) => {
    if (currentStep === StepEnum.Major) {
      setAnswers((prevAnswers) => ({
        ...prevAnswers,
        ["major"]: event,
      }));
    } else if (currentStep === StepEnum.Activity) {
      //TODO : add datalayer push for gtm

      setAnswers((prevAnswers) => ({
        ...prevAnswers,
        ["activity"]: event,
      }));
    }
    setIsNextButtonDisabled(false);
  };

  //* callback
  const handleMultiTypeAheadSelect = (selectedOptions) => {
    // Join the selected options array into a string delimited by '|'
    const selectedOptionsString = selectedOptions.join(",");
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      ["characteristics"]: selectedOptionsString,
    }));

    setIsNextButtonDisabled(false);
  };

  // * callback
  const carouselOptionSelectCallback = (selectedOption) => {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      ["location"]: selectedOption,
    }));

    setIsNextButtonDisabled(false);
  };

  //*carousel select callback so we can manage state
  const handleCarouselSelectCallback = (carouselType) => {
    setCarouselType(carouselType);
  }

  //* add to current step
  const nextStep = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  // * subtract from current step
  const previousStep = () => {
    if (currentStep > 0 && carouselType === "") {
      setCurrentStep(currentStep - 1);
    }
    else {
      //we are in the carousel option select state and just need to go back to the main options select
      setCarouselType("");
      setIsNextButtonDisabled(true);
    }
  };

  //*perform the correct data layer push for the given step
  const pushDataLayer = (step) => {
    // ? need to subtract one from step because we're push the data captured in the previous step - I know this sucks but bear with me
    switch (--step) {
      case StepEnum.Characteristics:
        window.dataLayer.push({
          event: "GustiebotSteps",
          stepnumber: "1 express yourself",
          characteristic: answers[answerKey],
        });
        break;
      case StepEnum.Major:
        window.dataLayer.push({
          event: "GustiebotSteps",
          stepnumber: "2 interests",
          characteristic: answers[answerKey],
        });
        break;
      case StepEnum.Activity:
        window.dataLayer.push({
          event: "GustiebotSteps",
          stepnumber: "3 passion",
          characteristic: answers[answerKey],
        });
        break;
      case StepEnum.Location:
        break;

      default:
        console.log("datalayer push done elsewhere for " + step);
    }
  };

  //*perfrom data layer push for error page
  const pushErrorDataLayer = () => {
    window.dataLayer.push({
      event: "imageError"
    });
  }

  // This useEffect hook listens for changes in the currentStep state. When the currentStep changes, it performs two actions:
  // 1. It calls the pushDataLayer function with the currentStep to handle data layer updates.
  // 2. It updates the answerKey and placeholder states based on the current step in the form wizard.
  useEffect(() => {
    pushDataLayer(currentStep);

    switch (currentStep) {
      case StepEnum.Characteristics:
        setAnswerKey("characteristics");
        break;
      case StepEnum.Major:
        setAnswerKey("major");
        setPlaceHolder("Search from our areas of study.");
        break;
      case StepEnum.Activity:
        setAnswerKey("activity");
        setPlaceHolder("Search from a list of interests.");
        break;
      case StepEnum.Location:
        setAnswerKey("location");
        break;
      default:
        console.log("bad current step: " + currentStep);
    }
  }, [currentStep]);

  //* listen to change in answer key to set next button disable
  useEffect(() => {
    //disable the next button if we don't have an answer
    const locationFlag = (answerKey === "location" && carouselType === "");
    setIsNextButtonDisabled(answers[answerKey] === null || answers[answerKey] === "" || locationFlag);
  }, [answerKey]);

  //* use effect to change logo to black for form wizard
  useEffect(() => {
    if (showFormWizard) {
      setLogoImage(blackTextLogo);
    }
  }, [showFormWizard]);

  // *use effect to change logo back to white on results page
  useEffect(() => {
    if (isLoading) {
      setLogoImage(whiteTextLogo);
    }
  }, [isLoading]);

  //make call to API
  const handleSubmit = async () => {
    //set loading screen and hide question
    //? move results showing logic to results component and show when openai response is present
    setIsLoading(true);

    //GTM data layer push for location
    window.dataLayer.push({
      event: "GustiebotSteps",
      event: `5 Location`,
      characteristic: answers[answerKey],
    });

    const request_url = `${config.BACKEND_URL}/gpt/process-answers`;
    try {
      const response = await fetch(request_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(answers),
      });

      //validate the response and handle errors
      if (response.ok) {
        const result = await response.json();
        
        if(result.image !== "") {
          setOpenAIResponse(result); // Store the response for passing to ResultsComponent
          setIsLoading(false);
          setShowResults(true);
  
          //gtm data layer push to signify successful card generation
          window.dataLayer.push({
            event: "cardGenerated",
          });
        }
        else {
          setShowError(true);
          pushErrorDataLayer();
        }
      } else {
        setShowError(true);
        pushErrorDataLayer();
      }
    } catch (error) {
      setShowError(true);
      pushErrorDataLayer();
    }
  };

  return (
    <div className={`form-wizard ${!showResults ? "skinny" : ""}`}>
      {!showResults && (
        <ProgressBar
          currentStep={currentStep + 1}
          totalSteps={questions.length}
        />
      )}

      <LoadingScreen
        setAppBackgroundColor={setAppBackgroundColor}
        isVisible={isLoading}
        showError={showError}
      />
      {!showResults && !isLoading ? (
        <>
          {currentStep == StepEnum.Major && (
            <>
              <img
                className="polaroid-background-top-left"
                src={whatInterestsYouLeft}
                alt="Polaroid Background"
              />
              <img
                className="polaroid-background-bottom-right"
                src={whatInterestsYouRight}
                alt="Polaroid Background"
              />
            </>
          )}
          {currentStep == StepEnum.Activity && (
            <>
              <img
                className="polaroid-background-bottom-left"
                src={yourPassionLeft}
                alt="Polaroid Background"
              />
              <img
                className="polaroid-background-top-right"
                src={yourPassionRight}
                alt="Polaroid Background"
              />
            </>
          )}
          <div className="question-content">
            <h2 className="question-header">{questions[currentStep].header}</h2>
            <p className="header-subtext">{questions[currentStep].subtext}</p>
          </div>
          {currentStep === StepEnum.ImageUpload ? (
            <>
              <img
                className="polaroid-background-top-left"
                src={whoYouAreLeft}
                alt="Polaroid Background"
              />
              <img
                className="polaroid-background-bottom-right"
                src={whoYouAreRight}
                alt="Polaroid Background"
              />
              <ImageUpload
                options={questions[currentStep].options}
                imageUploadCallback={handleImageSelect}
                ethnicitySelectCallback={ethnicitySelectCallback}
                genderSelectCallback={genderSelectCallback}
                onSkip={handleSkip}
                onContinueCallback={nextStep}
              ></ImageUpload>
            </>
          ) : currentStep === StepEnum.Characteristics ? (
            <>
              <img
                className="polaroid-background-bottom-left"
                src={expressYourselfLeft}
                alt="Polaroid Background"
              />
              <img
                className="polaroid-background-top-right"
                src={expressYourselfRight}
                alt="Polaroid Background"
              />
              <MultiSelectTypeahead
                options={questions[currentStep].options}
                // Assuming you have a handler function to update the answers state with selected options
                onOptionsSelect={handleMultiTypeAheadSelect}
                errorCallback={errorCallback}
              />
              <NavigationButtons
                currentStep={currentStep}
                onPrevious={previousStep}
                onNext={nextStep}
                onSubmit={handleSubmit}
                isPreviousDisabled={currentStep === 0}
                isNextDisabled={isNextButtonDisabled}
                isLastStep={currentStep === questions.length - 1}
              />
            </>
          ) : currentStep === StepEnum.Location ? (
            <>
              <img
                className="polaroid-background-top-left"
                src={whereYouSeeYourselfLeft}
                alt="Polaroid Background"
              />
              <img
                className="polaroid-background-bottom-right"
                src={whereYouSeeYourselfRight}
                alt="Polaroid Background"
              />
              <MultiCarouselSelect
                optionSelectCallback={carouselOptionSelectCallback}
                carouselSelectCallback={handleCarouselSelectCallback}
                ct={carouselType}
              />
              <NavigationButtons
                onPrevious={previousStep}
                onNext={nextStep}
                onSubmit={handleSubmit}
                isPreviousDisabled={currentStep === 0}
                isNextDisabled={isNextButtonDisabled}
                isLastStep={currentStep === questions.length - 1}
              />
            </>
          ) : (
            <>
              <TypeaheadInput
                options={questions[currentStep].options}
                selectedOption={answers[answerKey]}
                handleSelectOption={handleTypeAheadSelect}
                placeHolder={placeholder}
              />
              <NavigationButtons
                onPrevious={previousStep}
                onNext={nextStep}
                onSubmit={handleSubmit}
                isPreviousDisabled={currentStep === 0}
                isNextDisabled={isNextButtonDisabled}
                isLastStep={currentStep === questions.length - 1}
              />
            </>
          )}
        </>
      ) : (
        <ResultsComponent
          setAppBackgroundColor={setAppBackgroundColor}
          openAIResponse={openAIResponse}
          onNewStart={handleStartNewDesign}
        />
      )}
    </div>
  );
}

export default FormWizard;
